import React, { useState, useEffect, useRef } from "react";
import PlayerHand from "./TableComponents/PlayerHand";
import { useGameContext } from "../../context/gameContext/gameContext";
import ScoreBoard from "./TableComponents/ScoreBoard";
import {
  Button,
  Box,
  Flex,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import BiddingOptions from "./TableComponents/BiddingOptions";
import TrickContainer from "./TableComponents/TrickContainer";
import TrumpOptions from "./TableComponents/TrumpOptions";
import RoundSummary from "./TableComponents/RoundSummary";
import useWindowSize from "react-use/lib/useWindowSize";
import Confetti from "react-confetti";
import Redeal from "./TableComponents/Redeal";
import PreviousTrickOverlay from "./TableComponents/PreviousTrickOverlay";
import TableHamburger from "./TableComponents/TableHamburger";
import { useNavigate } from "react-router-dom";
import DealDeck from "./TableComponents/DealDeck";
import PlayedCard from "./TableComponents/PlayedCard";
import playCardSound from "../../assets/playc.ogg";
import playDealSound from "../../assets/dealinghands.mp3";
import whooshSound from "../../assets/whoosh.wav";
import { GiClubs, GiSpades, GiHearts, GiDiamonds } from "react-icons/gi";

import {
  dealCards,
  assignInitialDealer,
  updateCardProperties,
  updateCardPropertiesAfter,
  sortCardsByTrump,
  initialState,
} from "../../utils/cardUtils";
import { chooseCardBySearch } from "../../utils/ai/search";
import { isBotSeat } from "../../utils/ai/botController";
import { GAME_HISTORY_NAMES } from "../../utils/gameHistoryNames";
import { useAppContext } from "../../context/appContext/appContext";
import axios from "axios";
import "./TableComponents/Deck.css";

const Table = () => {
  const badgeIconSize = useBreakpointValue({ base: 12, md: 16, lg: 20 }) ?? 20;
  const [playersInfo, setPlayersInfo] = useState(initialState);

  const [isGameNotStarted, setIsGameNotStarted] = useState(true);
  const [isBiddingRound, setIsBiddingRound] = useState(false);
  const [isTrumpRound, setIsTrumpRound] = useState(false);
  const [isTrumpSelected, setIsTrumpSelected] = useState(false); // game begins if true
  const [dealer, setDealer] = useState(null);
  const [isRedeal, setIsRedeal] = useState(false);
  const [isDealer, setIsDealer] = useState(false);
  const [trickCount, setTrickCount] = useState(0);
  const [trick, setTrick] = useState([]);
  const [trickOver, setTrickOver] = useState(false);
  const [isRoundOver, setIsRoundOver] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [fourSubmitted, setFourSubmitted] = useState(false);
  const [dealDeck, setDealDeck] = useState(false);
  const [isDealt, setIsDealt] = useState(false);
  const [dealDelay, setDealDelay] = useState(100);
  const [side, setSide] = useState("");
  const [cardUrl, setCardUrl] = useState(null);
  const [trickAwardClass, setTrickAwardClass] = useState(null);
  const [botTrumpChoice, setBotTrumpChoice] = useState(null);
  const [showAllCards, setShowAllCards] = useState(false);
  const [cardStartRect, setCardStartRect] = useState(null);
  const [previousTrick, setPreviousTrick] = useState([]);
  const [showPreviousTrick, setShowPreviousTrick] = useState(false);

  const {
    username,
    botNames,
    numPlayers,
    gameMode,
    playerBids,
    trickSuit,
    winningBid,
    currentTrick,
    winningScore,
    currentTurn,
    setCurrentTurn,
    updateCurrentTrick,
    trump,
    resetCurrentTrick,
    setTrickSuit,
    awardTrickToWinner,
    awardTrickToPlayer,
    setTrickLeader,
    resetTrickSuit,
    wonTricks,
    wonTricksByPlayer,
    updateScores,
    updateIndividualScores,
    individualScores,
    individualPoints,
    resetBiddingRound,
    teamAScore,
    teamBScore,
    resetCurrentTurn,
    playAgainSameSettings,
    showTrickCardWinner,
  } = useGameContext();

  const { user } = useAppContext();

  useEffect(() => {
    currentTrickComplete();
  }, [currentTrick, trickSuit]);

  const botActingRef = useRef(false);

  // Whenever it becomes a bot seat's turn during active trick-play, have
  // the AI pick a card and "click" it the same way a human would.
  useEffect(() => {
    if (!isTrumpSelected || isBiddingRound || isTrumpRound) return;
    if (trickOver || isRoundOver || isGameOver) return;
    if (!currentTurn || !isBotSeat(currentTurn)) return;
    if (side || botActingRef.current) return;

    const seatIndex = Number(currentTurn[1]) - 1;
    const hand = playersInfo[seatIndex]?.hand;
    if (!hand || hand.length === 0) return;

    botActingRef.current = true;
    // Cutthroat awards tricks into wonTricksByPlayer, not wonTricks (which
    // stays empty all game in that mode) - reading only wonTricks here would
    // make every previously-won card look like it's still in the deck to
    // the bot's opponent-hand reconstruction, for every trick but the
    // current one.
    const playedCardsAll =
      gameMode === "Cutthroat"
        ? [
            ...Object.values(wonTricksByPlayer).flat(),
            ...currentTrick,
          ]
        : [...wonTricks.teamA, ...wonTricks.teamB, ...currentTrick];
    const handSizes = {
      P1: playersInfo[0].hand.length,
      P2: playersInfo[1].hand.length,
      P3: playersInfo[2].hand.length,
      P4: playersInfo[3].hand.length,
    };
    const mySeat = currentTurn;
    // The bidder must lead trick 1 with trump (if they hold any) - the
    // traditional "pitch."
    const mustLeadTrump =
      trickCount === 0 && trickSuit === "" && mySeat === winningBid.player;

    const timer = setTimeout(() => {
      const chosen = chooseCardBySearch({
        mySeat,
        myHand: hand,
        trick: currentTrick,
        trickSuit,
        trump,
        wonTricks,
        trickCount,
        winningBid,
        playedCardsAll,
        handSizes,
        mustLeadTrump,
        numPlayers,
      });
      botActingRef.current = false;
      playCard({
        target: { dataset: { suit: chosen.suit, card: chosen.details.card } },
      });
    }, 700);

    return () => {
      clearTimeout(timer);
      botActingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTurn, isTrumpSelected, trickOver]);

  const navigate = useNavigate();

  const { p1bid, p2bid, p3bid, p4bid } = playerBids;
  const { player } = winningBid;
  const { width, height } = useWindowSize();

  function playSound(sound) {
    const audio = new Audio(sound);
    audio.play();
  }

  // master function for entire single game - only call when new game begins
  const startGame = () => {
    initializeBiddingRound();
  };

  // initializes a bidding round
  const initializeBiddingRound = (redeal) => {
    setDealDeck(true);
    playSound(playDealSound);
    setIsGameNotStarted(false);
    setBotTrumpChoice(null);
    setPreviousTrick([]);
    setShowPreviousTrick(false);
    let randomDealer;
    if (isDealer && redeal) {
      randomDealer = dealer;
    } else if (isDealer && !redeal) {
      // Was hardcoded to wrap after seat 4 - for 3-player Cutthroat this
      // would advance a dealer of 3 to a nonexistent 4 instead of wrapping
      // to 1, cascading into every other numPlayers-aware turn/dealer
      // calculation for the rest of the game.
      randomDealer = dealer === Number(numPlayers) ? 1 : dealer + 1;
    } else {
      randomDealer = assignInitialDealer(numPlayers);
    }
    // const randomDealer = isDealer ? dealer : assignInitialDealer(numPlayers);

    let dealtHands = dealCards(numPlayers, randomDealer);
    const timeoutSeconds = isBiddingRound ? 0 : 3000;
    setDealer(randomDealer);
    dealFeatures();
    setTimeout(() => {
      let updatedHands = playersInfo.map((players, i) => {
        // dealtHands only has one entry per actual player (3 for 3-player
        // Cutthroat), but playersInfo always has all 4 seats - fall back to
        // an empty hand for any seat past numPlayers instead of leaving
        // `hand` undefined, which several places (e.g. the bot-turn effect's
        // handSizes lookup) read the length of unconditionally.
        return { ...players, hand: dealtHands[i] || [] };
      });
      setDealDeck(false);
      setIsDealt(false);
      setPlayersInfo(updatedHands);
      setIsDealer(true);
      setIsGameNotStarted(false);
      setIsBiddingRound(true);
      setIsTrumpSelected(false);
    }, timeoutSeconds);
  };

  // testing function - it works and updates the deal
  const updateDealer = () => {
    if (dealer >= numPlayers) {
      setDealer(1);
    } else {
      setDealer((prev) => (prev += 1));
    }
  };

  // triggers redeal. tells state if there is a dealer determined or not. then sets ui to showcase new deal is underway
  const updateRedeal = (value) => {
    setIsRedeal((prev) => !prev);
    setIsBiddingRound(false);
    setTimeout(() => {
      setIsBiddingRound(true);
      setIsRedeal(false);
    }, 2000);
  };

  const renderPlayerHand = (playersInfo) => {
    return playersInfo[Number(currentTurn[1]) - 1]?.hand;
  };

  // figures out if player doesn't have any trump or a card with same suit of the lead
  const currentTurnHand = renderPlayerHand(playersInfo);

  const suits = ["hearts", "spades", "clubs", "diamonds"];
  const missingSuits = suits.filter(
    (suit) => !currentTurnHand?.some((card) => card.suit === suit)
  );

  // might not need include trump - need to wait and see until lead suit isn't a trump to see what happens

  // function used to play a card, card ends up in center of table in trick container
  const playCard = (e) => {
    if (isBiddingRound || isTrumpRound || fourSubmitted || side) {
      return;
    }
    playSound(playCardSound);
    let newTrick = trickSuit;
    const includeTrickLead = missingSuits.includes(newTrick);
    const includeTrump = missingSuits.includes(trump);

    // Trump can always be played, regardless of what you hold. The only
    // restriction: if you hold the led suit, you must play either the led
    // suit or trump - you can't dump an unrelated third suit. (The
    // "&& !includeTrump" this used to also require was the bug: it let a
    // player with the led suit but no trump dump any off-suit, non-trump
    // card instead of following or trumping in.)
    if (
      e.target.dataset.suit !== trump &&
      e.target.dataset.suit !== newTrick &&
      !includeTrickLead &&
      trickSuit !== ""
    ) {
      return;
    }

    // The bidder must lead the very first trick of the hand with trump (the
    // traditional "pitch") if they hold any - blocked here the same way as
    // any other illegal move.
    const isFirstLead = trickSuit === "" && trickCount === 0;
    if (
      isFirstLead &&
      currentTurn === winningBid.player &&
      !includeTrump &&
      e.target.dataset.suit !== trump
    ) {
      return;
    }

    if (trickSuit === "") {
      newTrick = e.target.dataset.suit;
      setTrickSuit(newTrick);
    }

    // Capture the real on-screen position/size of the card being played
    // before it's removed from the hand, so the flying animation starts
    // from its actual spot rather than a guessed position. Looked up by
    // data attributes (not e.target directly) so this works whether the
    // play came from a real click or a bot's synthetic event.
    const cardEl = document.querySelector(
      `img[data-owner="${currentTurn}"][data-card="${e.target.dataset.card}"]`
    );
    setCardStartRect(cardEl ? cardEl.getBoundingClientRect() : null);

    // Was hardcoded to wrap after seat 4 - the same bug as the trick.length
    // check above, just in the turn-advancement path: for 3-player
    // Cutthroat, once P3 played this always computed 4 (nonexistent) instead
    // of wrapping to 1, permanently handing the turn to a phantom P4 that
    // can never act and freezing the game.
    const seatCount = Number(numPlayers);
    const num =
      Number(currentTurn[1]) === seatCount ? 1 : Number(currentTurn[1]) + 1;
    let takeCard;
    let newinfo = playersInfo;
    const newHands = newinfo.map((person) => {
      const hands = person.hand.filter((card) => {
        if (card.details.card !== e.target.dataset.card) {
          return card;
        } else {
          takeCard = card;
          return false;
        }
      });

      const updatedHand = hands.map((card) => {
        const newCard = updateCardPropertiesAfter(
          card,
          trump,
          person,
          newTrick,
          includeTrickLead,
          includeTrump
        );

        return newCard;
      });

      return { ...person, hand: updatedHand };
    });

    console.log(takeCard.details.owner);
    setCardUrl(takeCard.details.url);
    takeCard.details.faceUp = true;
    // shouldDarken doubles as "illegal to play right now" while a card is
    // in-hand and "lost the trick" once it's in the trick area - a card can
    // carry a stale true from the former into the latter if it happened to
    // be flagged unplayable earlier in this same trick's sequence before
    // being legally played. Clear it here so a just-played card always
    // starts looking normal; updateTrickForWinner sets it for real once the
    // trick actually resolves.
    takeCard.shouldDarken = false;
    // start an animation here showing card being played to trick container
    setSide(true);

    playCardUpdateTable(newHands);
    setTimeout(() => {
      setSide(false);
      setCardUrl(null);
      updateCurrentTrick(takeCard);
      setLocalTrickState(takeCard);
      setCurrentTurn(num);
    }, 250);
  };

  const updateTrickForWinner = (winningCard) => {
    let newTrick = [...trick];
    const uiTrick = newTrick.map((card) => {
      if (card?.details.card !== winningCard?.details.card) {
        return { ...card, shouldDarken: true };
      } else return card;
    });

    setTrick(uiTrick);
  };

  const currentTrickComplete = () => {
    if (trickCount < 6) {
      // A trick is complete once every active seat has played into it - 4
      // for 2v2 Teams and 4-player Cutthroat, but only 3 for 3-player
      // Cutthroat (there's no P4 to wait on). Without this, a 3-player
      // trick would never reach 4 cards and would simply never complete.
      if (trick.length === Number(numPlayers)) {
        setTrickCount((prev) => (prev += 1));
        setTrickOver(true);
        setCurrentTurn("");

        // Computed once here and reused for both the darken-losers timer
        // and beginNextTrick below, instead of being recalculated later -
        // recalculating (and re-triggering per-candidate state updates
        // along the way) was scheduling multiple overlapping darken timers
        // per trick, some of which could fire after the next trick had
        // already started, darkening a card that had just been played.
        const winnerCard = highestCard(currentTrick, trickSuit);

        // Snapshot the trick (with losers darkened, same as the live
        // container is about to show) into its own state before it gets
        // collected/cleared, so "Previous Trick" can still show it after.
        setPreviousTrick(
          trick.map((card) =>
            card?.details.card !== winnerCard?.details.card
              ? { ...card, shouldDarken: true }
              : card
          )
        );

        setTimeout(() => {
          updateTrickForWinner(winnerCard);
        }, 1000);

        setTimeout(() => {
          beginNextTrick(winnerCard);
          setTrickOver(false);
        }, 2200);
        return;
      }
    } else {
      const isGameOver =
        gameMode === "Cutthroat" ? updateScoreIndividual() : updateScore();
      if (isGameOver) {
        setIsRoundOver(true);
        setIsGameOver(true);
        // playAgainSameSettings();
        setIsTrumpSelected(false);
        setTrickCount(0);
        return;
      } else {
        setIsRoundOver(true);
        setIsTrumpSelected(false);
      }
    }
  };

  const beginNextTrick = (winnerCard) => {
    const winningOwner = winnerCard.details.owner;
    awardTrick(winningOwner);
    playSound(whooshSound);
    setTimeout(() => {
      setTrickLeader(winningOwner);
      // Cutthroat: the trick's points go to whichever individual played the
      // winning card. 2v2 Teams: unchanged, still P1+P3 vs P2+P4.
      if (gameMode === "Cutthroat") {
        awardTrickToPlayer(winningOwner, trick);
      } else {
        const winningTeam =
          winningOwner === "P1" || winningOwner === "P3" ? "teamA" : "teamB";
        awardTrickToWinner(winningTeam, trick);
      }
      resetTrickSuit();

      const newHands = playersInfo.map((person) => {
        const hands = person.hand.map((card) => {
          return { ...card, shouldDarken: false };
        });

        return { ...person, hand: hands };
      });
      setPlayersInfo(newHands);

      const number = Number(winningOwner.substr(1, 1));
      setCurrentTurn(number);

      // resets trick container to empty array
      setTrickAwardClass(null);
      resetCurrentTrick();
      setTrick([]);
    }, 400);

    //  have a counter keep track of what trick you are on, once tricks === 6, calculate score and update it, then pass in initalizeBiddingRound function
  };

  // Pure: figures out which card wins the trick without any side effects,
  // so it's safe to call from multiple places without triggering extra
  // renders/timers.
  const highestCard = (cards, trickSuit) => {
    const hasTrumpCards = cards.some((card) => card.details.trump);
    const pool = hasTrumpCards
      ? cards.filter((card) => card.details.trump)
      : cards.filter((card) => card.suit === trickSuit);

    let winner = pool[0];
    for (let i = 1; i < pool.length; i++) {
      if (pool[i].details.value > winner.details.value) winner = pool[i];
    }
    return winner;
  };

  // sent as a prop to trump options to determine if winning bidder is missing some suits, disables the buttons of those suits
  const winningBiddersHand =
    player !== "" ? playersInfo[Number(player[1]) - 1].hand : "";

  // after trump is chose, players cards update to reflect if they're a trump or not, trump cards shift to the right of their hand
  // all cards that are not trumps or trickSuits are disabled from being played, if player has neither, then all of their cards are enabled for play

  const beginTrickRound = (trump) => {
    const bidderSeat = winningBid.player;
    const updatePlayersHands = playersInfo.map((person) => {
      // The bidder must lead trick 1 with trump (the traditional "pitch")
      // if they hold any - dim everything else in their hand so that's
      // clear, same as any other must-follow restriction.
      const mustLeadTrump =
        person.player === bidderSeat &&
        person.hand.some((card) => card.suit === trump);

      const allCardsNotTrumpOrTrickSuit = person.hand.every(
        (card) => card.suit !== trump && card.suit !== trickSuit
      );

      if (allCardsNotTrumpOrTrickSuit && !mustLeadTrump) {
        return person;
      }

      const updatedHand = person.hand
        .map((card) => {
          const updated = updateCardProperties(card, trump, trickSuit);
          return mustLeadTrump
            ? { ...updated, shouldDarken: card.suit !== trump }
            : updated;
        })
        .sort(sortCardsByTrump);

      return { ...person, hand: updatedHand };
    });

    playCardUpdateTable(updatePlayersHands);
    setIsTrumpRound(false);
    setIsTrumpSelected(true);
  };

  const continueToNextRound = () => {
    setIsRoundOver(false);
    resetBiddingRound();
    updateDealer();
    setTrickCount(0);
    resetCurrentTurn();
    initializeBiddingRound(false);
  };

  const playCardUpdateTable = (value) => {
    setPlayersInfo(value);
  };

  const setLocalTrickState = (card) => {
    setTrick([...trick, card]);
  };

  const updateScore = () => {
    const { teamA, teamB } = wonTricks;
    const { bid, team } = winningBid;

    const combinedArrays = [...teamA, ...teamB];

    const onlyTrumpCards = combinedArrays.filter((card) => card?.details.trump);

    let highestValue = -1;
    let highestValueCard = null;
    let lowestValue = 200;
    let lowestValueCard = null;
    let jackCard = null;
    let jackCardObj = null;
    let teamAscore = 0;
    let teamBscore = 0;
    let us = 0;
    let them = 0;
    let teamAGame = 0;
    let teamBGame = 0;
    let success = true;

    for (let i = 0; i < onlyTrumpCards?.length; i++) {
      const card = onlyTrumpCards[i];
      if (card?.details.value > highestValue) {
        highestValue = card?.details.value;
        highestValueCard = card;
      }
      if (card?.details.value < lowestValue) {
        lowestValue = card?.details.value;
        lowestValueCard = card;
      }
      if (card?.details.value === 11) {
        jackCard = card?.details.card;
        jackCardObj = card;
      }
    }

    for (let i = 0; i < teamA.length; i++) {
      const card = teamA[i];
      if (card.details.gamePoints) {
        teamAGame += card?.details.gamePoints;
      }
    }

    for (let i = 0; i < teamB.length; i++) {
      const card = teamB[i];
      if (card?.details.gamePoints) {
        teamBGame += card?.details.gamePoints;
      }
    }

    const high = teamA.includes(highestValueCard);
    const low = teamA.includes(lowestValueCard);

    if (high) {
      teamAscore++;
      us++;
    } else {
      teamBscore++;
      them++;
    }
    if (low) {
      teamAscore++;
      us++;
    } else {
      teamBscore++;
      them++;
    }
    if (jackCard !== null) {
      if (teamA.includes(jackCardObj)) {
        teamAscore++;
        us++;
      } else {
        teamBscore++;
        them++;
      }
    }
    if (teamAGame > teamBGame) {
      teamAscore++;
      us++;
    } else {
      teamBscore++;
      them++;
    }

    if (team === "teamA" && bid > teamAscore) {
      teamAscore = 0;
      teamAscore -= bid;
      success = false;
    }
    if (team === "teamB" && bid > teamBscore) {
      teamBscore = 0;
      teamBscore -= bid;
      success = false;
    }

    let highWinner = high ? "Us" : "Them";
    let lowWinner = low ? "Us" : "Them";
    let gameWinner = teamAGame > teamBGame ? "Us" : "Them";
    let jackWinner =
      jackCard === null ? null : teamA.includes(jackCardObj) ? "Us" : "Them";

    const points = {
      high: { card: highestValueCard?.details.card, team: highWinner },
      low: { card: lowestValueCard?.details.card, team: lowWinner },
      game: { teamA: teamAGame, teamB: teamBGame, team: gameWinner },
      jack: { card: jackCard, team: jackWinner },
      us: us,
      them: them,
      usFinal: teamAscore,
      themFinal: teamBscore,
    };

    const newAScore = teamAscore + teamAScore;
    const newBScore = teamBscore + teamBScore;

    updateScores(newAScore, newBScore, points, success);
    checkGameOver(newAScore, newBScore, winningScore);
    const isGameOver = checkGameOver(newAScore, newBScore, winningScore);
    return isGameOver;
  };

  const checkGameOver = (a, b, winningScore) => {
    // Was: a tie at/above the winning score meant "keep playing" (no
    // tie-breaker existed). Now the tie-breaker rule below
    // (teamAWinsGame) resolves that case instead of extending the game,
    // so both teams crossing the threshold ends the game exactly like
    // just one team crossing does.
    return a >= winningScore || b >= winningScore;
  };

  // 2v2 Teams only: if both teams are at/above the winning score after the
  // same hand, the team that bid that hand wins outright, regardless of
  // which score is higher - overrides the plain a>b comparison used
  // everywhere else "who won" is displayed (confetti, RoundSummary's
  // heading and winner-circle). Falls back to the normal higher-score-wins
  // comparison whenever only one team (or neither) has reached the
  // threshold, so the single-team-crosses case is completely unchanged.
  const teamAWinsGame = (a, b, winningScore, biddingTeam) =>
    a >= winningScore && b >= winningScore ? biddingTeam === "teamA" : a > b;

  // Records the human player's (always seat P1) win/loss the moment a game
  // actually ends, reusing the exact same "who really won" logic already
  // used for display (teamAWinsGame / individualPoints.winners) so this
  // can't drift from what RoundSummary shows. Only fires once per game-over
  // transition since isGameOver is the sole dependency. Guests (no user)
  // are never recorded, matching the Counting Game's pattern.
  useEffect(() => {
    if (!isGameOver || !user) return;

    const gameName =
      gameMode === "Cutthroat"
        ? GAME_HISTORY_NAMES.CUTTHROAT
        : GAME_HISTORY_NAMES.TEAMS;
    const humanWon =
      gameMode === "Cutthroat"
        ? individualPoints.winners.includes("P1")
        : teamAWinsGame(teamAScore, teamBScore, winningScore, winningBid.team);

    axios
      .post("/api/v1/game-history", {
        gameName,
        result: humanWon ? "win" : "loss",
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGameOver]);

  // Cutthroat counterpart to updateScore/checkGameOver: same High/Low/Jack/
  // Game point rules and same "a tie at/above the winning score isn't a win
  // yet" rule, generalized from exactly 2 teams to N individual players.
  const updateScoreIndividual = () => {
    const activePlayers = Array.from(
      { length: Number(numPlayers) },
      (_, i) => `P${i + 1}`
    );

    const combinedArrays = activePlayers.flatMap(
      (p) => wonTricksByPlayer[p]
    );
    const onlyTrumpCards = combinedArrays.filter(
      (card) => card?.details.trump
    );

    let highestValue = -1;
    let highestValueCard = null;
    let lowestValue = 200;
    let lowestValueCard = null;
    let jackCard = null;
    let jackCardObj = null;

    for (let i = 0; i < onlyTrumpCards.length; i++) {
      const card = onlyTrumpCards[i];
      if (card?.details.value > highestValue) {
        highestValue = card?.details.value;
        highestValueCard = card;
      }
      if (card?.details.value < lowestValue) {
        lowestValue = card?.details.value;
        lowestValueCard = card;
      }
      if (card?.details.value === 11) {
        jackCard = card?.details.card;
        jackCardObj = card;
      }
    }

    // Whichever player's own won-tricks bucket contains this card. Unlike
    // the 2-team version (where a card not being teamA's automatically
    // means it's teamB's), with N players there's no "the other one" to
    // fall back to - a null card (nobody ever won a trump card this round)
    // simply finds no owner and, below, awards nobody that point.
    const ownerOf = (card) =>
      card
        ? activePlayers.find((p) => wonTricksByPlayer[p].includes(card))
        : null;

    const gameScores = {};
    activePlayers.forEach((p) => {
      gameScores[p] = wonTricksByPlayer[p].reduce(
        (sum, card) => sum + (card?.details.gamePoints || 0),
        0
      );
    });

    // Ties go to whichever tied player comes first in seat order - the
    // 2-team version's equivalent tie (teamAGame === teamBGame) always
    // goes to teamB, so this is a different but equally arbitrary
    // deterministic choice; there's no way to reuse that exact rule for
    // more than 2 players.
    const gameWinner = activePlayers.reduce((best, p) =>
      gameScores[p] > gameScores[best] ? p : best
    );

    const highWinner = ownerOf(highestValueCard);
    const lowWinner = ownerOf(lowestValueCard);
    const jackWinner = jackCard !== null ? ownerOf(jackCardObj) : null;

    const roundScores = {};
    activePlayers.forEach((p) => {
      roundScores[p] = 0;
    });
    if (highWinner) roundScores[highWinner]++;
    if (lowWinner) roundScores[lowWinner]++;
    if (jackWinner) roundScores[jackWinner]++;
    roundScores[gameWinner]++;

    // Snapshot before the set penalty below can overwrite the bidder's
    // entry - mirrors team scoring's us/them (raw, pre-penalty) vs.
    // usFinal/themFinal (post-penalty) split, needed so the round-summary
    // UI can show both "Points Won" and "Adjusted Points" per player.
    const pointsWon = { ...roundScores };

    let success = true;
    const bidderSeat = winningBid.player;
    if (bidderSeat && winningBid.bid > roundScores[bidderSeat]) {
      roundScores[bidderSeat] = -winningBid.bid;
      success = false;
    }

    const newScores = { ...individualScores };
    activePlayers.forEach((p) => {
      newScores[p] = (individualScores[p] || 0) + roundScores[p];
    });

    const { isOver, winners } = checkGameOverIndividual(
      newScores,
      activePlayers,
      winningScore,
      bidderSeat
    );

    const points = {
      high: { card: highestValueCard?.details.card, player: highWinner },
      low: { card: lowestValueCard?.details.card, player: lowWinner },
      jack: { card: jackCard, player: jackWinner },
      game: { scores: gameScores, player: gameWinner },
      pointsWon,
      roundScores,
      winners,
    };

    updateIndividualScores(newScores, points, success);

    return isOver;
  };

  // Cutthroat tie-breaker, mirroring the 2v2 Teams rule (see
  // teamAWinsGame) generalized from 2 teams to N individual players.
  // "Leaders" are every player at/above the winning score, regardless of
  // whether their scores are equal to each other - once two or more
  // players have crossed the line, a higher-scoring non-bidder doesn't
  // automatically outrank a lower-scoring bidder, same as the team
  // version treats "both crossed" as one case regardless of which score
  // is bigger.
  const checkGameOverIndividual = (
    scoresBySeat,
    activePlayers,
    winningScore,
    bidderSeat
  ) => {
    const leaders = activePlayers.filter(
      (p) => scoresBySeat[p] >= winningScore
    );
    if (leaders.length === 0) return { isOver: false, winners: [] };
    if (leaders.length === 1) return { isOver: true, winners: leaders };
    if (bidderSeat && leaders.includes(bidderSeat)) {
      return { isOver: true, winners: [bidderSeat] };
    }
    return { isOver: true, winners: leaders };
  };

  const playSameSettings = () => {
    playAgainSameSettings();
    setIsGameOver(false);
    setIsGameNotStarted(true);
    setIsRoundOver(false);
    navigate("/play/play-game");
  };

  // Shows the chosen trump suit + winning bid amount right next to whichever
  // player's/bot's name actually won the bid, instead of a fixed corner badge.
  const renderTrumpBadge = (seat) => {
    if (!isTrumpSelected || !trump || winningBid.player !== seat) return null;
    return (
      <Flex align="center" gap="2px">
        <Box as="span" fontSize="var(--name-font)">
          (
        </Box>
        {trump === "diamonds" && (
          <GiDiamonds color="red" size={badgeIconSize} />
        )}
        {trump === "clubs" && <GiClubs size={badgeIconSize} />}
        {trump === "hearts" && <GiHearts color="red" size={badgeIconSize} />}
        {trump === "spades" && <GiSpades size={badgeIconSize} />}
        <Box as="span" fontSize="var(--name-font)">
          {winningBid.bid})
        </Box>
      </Flex>
    );
  };

  const renderPlayerCards = (playerId, playersInfo, top) => {
    const players = playersInfo.find((p) => p.player === playerId);
    if (!players) return null;
    return (
      <PlayerHand
        hand={players.hand}
        id={players.player}
        owner={players.player}
        showAllCards={showAllCards}
        playerInfo={playersInfo}
        playCardUpdateTable={playCardUpdateTable}
        isTrumpRound={isTrumpRound}
        isBiddingRound={isBiddingRound}
        initializeBiddingRound={initializeBiddingRound}
        playCard={playCard}
        animate={true}
        trickOver={trickOver}
      />
    );
  };

  const player1Cards = renderPlayerCards("P1", playersInfo);
  const player2Cards = renderPlayerCards("P2", playersInfo);
  const player3Cards = renderPlayerCards("P3", playersInfo);
  const player4Cards = renderPlayerCards("P4", playersInfo);

  const dealFeatures = () => {
    setIsDealt(true);
  };

  const awardTrick = (owner) => {
    if (owner === "P1") {
      setTrickAwardClass("trickCollect1");
    } else if (owner === "P2") {
      setTrickAwardClass("trickCollect2");
    } else if (owner === "P3") {
      setTrickAwardClass("trickCollect3");
    } else setTrickAwardClass("trickCollect4");
  };

  return (
    <>
      <div className="table">
        {side && (
          <PlayedCard side={side} url={cardUrl} startRect={cardStartRect} />
        )}
        <div
          className={
            isGameNotStarted || dealDeck
              ? "player player2"
              : "player player2Game"
          }
        >
          {p2bid && <Box className="bidAmount p2">{p2bid}</Box>}

          <div className="nameRow">
            <div className="name">{botNames.P2}</div>
            {renderTrumpBadge("P2")}
            {dealer === 2 && <p className="dealer2">DEALER</p>}
          </div>
          <div className="playerHand">{player2Cards}</div>
        </div>

        <div
          className={
            isGameNotStarted || dealDeck
              ? "player player3"
              : "player player3Game"
          }
        >
          {p3bid && <Box className="bidAmount">{p3bid}</Box>}
          <div className="nameRow">
            <div className="name">{botNames.P3}</div>
            {renderTrumpBadge("P3")}
          </div>
          {dealer === 3 && <p className="dealer3">DEALER</p>}
          <div className="playerHand"> {player3Cards}</div>
        </div>

        <div>
          {isGameNotStarted && <Button onClick={startGame}>Start Game</Button>}
          {!isRedeal && !isBiddingRound && dealDeck && (
            <DealDeck
              isDealt={isDealt}
              dealDelay={dealDelay}
              setDealDelay={setDealDelay}
              dealer={dealer}
              numPlayers={numPlayers}
            />
          )}
          {isBiddingRound && (
            <BiddingOptions
              dealer={dealer}
              numPlayers={numPlayers}
              gameMode={gameMode}
              setTrump={setIsTrumpRound}
              setBid={setIsBiddingRound}
              initializeBiddingRound={initializeBiddingRound}
              redeal={updateRedeal}
              setFourSubmitted={setFourSubmitted}
              playersInfo={playersInfo}
              onBotBidTrump={setBotTrumpChoice}
            />
          )}
          {isTrumpRound && (
            <TrumpOptions
              beginTrickRound={beginTrickRound}
              winningBiddersHand={winningBiddersHand}
              botTrumpChoice={botTrumpChoice}
            />
          )}
          {isTrumpSelected && (
            <TrickContainer
              playerInfo={playersInfo}
              playCardUpdateTable={playCardUpdateTable}
              trick={trick}
              trickAwardClass={trickAwardClass}
            />
          )}
          {isGameOver &&
            teamAWinsGame(
              teamAScore,
              teamBScore,
              winningScore,
              winningBid.team
            ) && <Confetti width={width} height={height} />}
          {isRedeal && <Redeal />}
          {isRoundOver && (
            <RoundSummary
              gameOver={isGameOver}
              continueToNextRound={continueToNextRound}
              setIsGameNotStarted={setIsGameNotStarted}
              playSameSettings={playSameSettings}
            />
          )}
        </div>
        <div
          className={
            isGameNotStarted || dealDeck
              ? "player player1"
              : "player player1Game"
          }
        >
          {p1bid && <Box className="bidAmount">{p1bid}</Box>}

          <div className="nameRow">
            <div className="name">{username}</div>
            {renderTrumpBadge("P1")}
          </div>
          {dealer === 1 && <p className="dealer">DEALER</p>}
          <div className="playerHand1">{player1Cards}</div>
        </div>

        {Number(numPlayers) !== 3 && (
          <div
            className={
              isGameNotStarted || dealDeck
                ? "player player4"
                : "player player4Game"
            }
          >
            {p4bid && <Box className="bidAmount p4">{p4bid}</Box>}

            <div className="nameRow">
              <div className="name">{botNames.P4}</div>
              {renderTrumpBadge("P4")}
              {dealer === 4 && <p className="dealer4">DEALER</p>}
            </div>
            <div className="playerHand">{player4Cards}</div>
          </div>
        )}

        <TableHamburger setIsGameOver={setIsGameOver} />
        <ScoreBoard />
        {!isGameNotStarted && (
          <Button
            position="absolute"
            left={{ base: "6px", md: "16px", lg: "30px" }}
            bottom={{ base: "6px", md: "16px", lg: "30px" }}
            size={{ base: "xs", md: "sm", lg: "md" }}
            onClick={() => setShowAllCards((prev) => !prev)}
            colorScheme={showAllCards ? "orange" : "gray"}
          >
            {showAllCards ? "Hide Cards" : "Show Cards"}
          </Button>
        )}
        {previousTrick.length > 0 && (
          <Button
            position="absolute"
            right={{ base: "6px", md: "16px", lg: "30px" }}
            bottom={{ base: "6px", md: "16px", lg: "30px" }}
            size={{ base: "xs", md: "sm", lg: "md" }}
            onClick={() => setShowPreviousTrick(true)}
          >
            Previous Trick
          </Button>
        )}
        {showPreviousTrick && (
          <PreviousTrickOverlay
            trick={previousTrick}
            onClose={() => setShowPreviousTrick(false)}
          />
        )}
      </div>
    </>
  );
};

export default Table;
